import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import type { LoanType, LoanWithDetails } from '@/types/booky'

const loanSelect = `
  id,
  loan_type,
  loaned_at,
  returned_at,
  book:booky_books!booky_loans_book_id_fkey(id, isbn13, title, authors, cover_url),
  friend:booky_friends!booky_loans_friend_id_fkey(id, name)
`

export const useLoans = (userId: string | undefined) => {
  const [activeLoans, setActiveLoans] = useState<LoanWithDetails[]>([])
  const [historyLoans, setHistoryLoans] = useState<LoanWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const normalizeLoans = (rows: unknown[]): LoanWithDetails[] => {
    return rows
      .map((row) => {
        const record = row as {
          id: number
          loan_type: LoanType
          loaned_at: string
          returned_at: string | null
          book: LoanWithDetails['book'] | LoanWithDetails['book'][]
          friend: LoanWithDetails['friend'] | LoanWithDetails['friend'][]
        }

        const book = Array.isArray(record.book) ? record.book[0] : record.book
        const friend = Array.isArray(record.friend) ? record.friend[0] : record.friend

        if (!book || !friend) {
          return null
        }

        return {
          id: record.id,
          loan_type: record.loan_type ?? 'on-loan',
          loaned_at: record.loaned_at,
          returned_at: record.returned_at,
          book,
          friend,
        }
      })
      .filter((loan): loan is LoanWithDetails => loan !== null)
  }

  const fetchLoans = useCallback(async () => {
    if (!userId) {
      setActiveLoans([])
      setHistoryLoans([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const [activeResult, historyResult] = await Promise.all([
        supabase
          .from('booky_loans')
          .select(loanSelect)
          .is('returned_at', null)
          .order('loaned_at', { ascending: false }),
        supabase
          .from('booky_loans')
          .select(loanSelect)
          .not('returned_at', 'is', null)
          .order('returned_at', { ascending: false })
          .limit(20),
      ])

      if (activeResult.error) {
        throw activeResult.error
      }

      if (historyResult.error) {
        throw historyResult.error
      }

      setActiveLoans(normalizeLoans((activeResult.data ?? []) as unknown[]))
      setHistoryLoans(normalizeLoans((historyResult.data ?? []) as unknown[]))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load loans'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void fetchLoans()
  }, [fetchLoans])

  const createLoan = async (bookId: number, friendId: number, loanType: LoanType = 'on-loan') => {
    if (!userId) {
      return { error: new Error('User not signed in') }
    }

    const { error } = await supabase.from('booky_loans').insert({
      user_id: userId,
      book_id: bookId,
      friend_id: friendId,
      loan_type: loanType,
      loaned_at: new Date().toISOString(),
    })

    if (!error) {
      await fetchLoans()
    }

    return { error }
  }

  const returnLoan = async (loanId: number) => {
    const { error } = await supabase
      .from('booky_loans')
      .update({ returned_at: new Date().toISOString() })
      .eq('id', loanId)

    if (!error) {
      await fetchLoans()
    }

    return { error }
  }

  return {
    activeLoans,
    historyLoans,
    loading,
    error,
    fetchLoans,
    createLoan,
    returnLoan,
  }
}
